/**
 * @module utils/columnSort/fromQB/buildDimensionSortItemsFromQB
 * @description 场景 1（未持久化 column_sort）：从 QB 维度区构建列排序中的「维度段」，并兼顾对比时间
 *
 * 返回两段数组，由 {@link buildColumnSortFromQB} 按固定顺序拼接：
 * `[对照组, leadingCompareTime, dimensions, metrics]`。
 *
 * 业务约定：
 * - compareTime 由上层传入（通常来自 detectCompareTime），本模块不扫描 columnFilters.compareTimes
 * - 日期字段在维度区：仅打 is_compare_date，column_display_* 保持 QB 字段原名（与 test 旧逻辑一致）
 * - 日期仅在筛选区：生成 leadingCompareTime 独立列，display 取自筛选区字段名，兜底「对比时间」
 */

import { SortableType } from '@dbl/core/chartEditor';
import type {
  IColumnFilter,
  IColumnSortItem,
} from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { COLUMN_DISPLAY_TEXT } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/constants';
import { getFieldIdentityKey, qbRowToColumnSortItem } from '../untils/fieldUtils';
import { normalizeColumnSortItem } from '../untils/keys';

/** 与 detectCompareTime / QB 联动 changeSet.compareTime 一致 */
export interface ICompareTimeInput {
  enabled: boolean;
  dateFieldKey?: string;
}

/** buildDimensionSortItemsFromQB 的返回值 */
export interface IBuildDimensionSortFromQBResult {
  /** 独立对比时间列（0 或 1 项）；拼在对照组之后、维度段之前 */
  leadingCompareTime: IColumnSortItem[];
  /** 维度段（按 QB 维度区顺序；对比日期字段在维度区时含 is_compare_date） */
  dimensions: IColumnSortItem[];
  /** 维度 identity 集合，供 buildMetricSortItemsFromQB 判断维指重名 */
  dimensionKeySet: Set<string>;
}

/**
 * 解析对比日期字段的 table 元数据（生成独立「对比时间」列时使用）。
 *
 * 日期字段可能只出现在筛选区而未拖入维度区，需从 columnFilters 兜底取 table_id/table_code。
 */
export function resolveCompareTimeFieldMeta(
  dateFieldKey: string,
  columnDimensions: unknown[],
  columnFilters?: unknown[],
): { table_id: string; table_code: string; column: string } {
  for (const raw of columnDimensions) {
    if (!raw || typeof raw !== 'object') {
      continue;
    }
    const row = raw as Record<string, unknown>;
    const name = String(row.column_name ?? row.column ?? '');
    if (name === dateFieldKey) {
      return {
        table_id: String(row.table_id ?? ''),
        table_code: String(row.table_code ?? 't0'),
        column: name,
      };
    }
  }
  if (Array.isArray(columnFilters)) {
    const match = columnFilters.find(f => {
      const filter = f as IColumnFilter;
      return filter.column_name === dateFieldKey;
    }) as IColumnFilter | undefined;
    if (match) {
      return {
        table_id: String(match.table_id ?? ''),
        table_code: match.table_code || 't0',
        column: match.column_name || dateFieldKey,
      };
    }
  }
  return { table_id: '', table_code: 't0', column: dateFieldKey };
}

/** 维度行是否为对比日期字段（与 findCompareFieldInSort 一致：按物理列名匹配） */
function isRowCompareDateField(
  row: Record<string, unknown>,
  dateFieldKey: string,
): boolean {
  return String(row.column_name ?? row.column ?? '') === dateFieldKey;
}

/** 独立对比时间列的 persist display（与 test fieldColumnSort/init 一致） */
export function resolveCompareTimeFilterDisplay(
  dateFieldKey: string,
  columnFilters?: unknown[],
): { column_display_cn: string; column_display_en: string } {
  if (Array.isArray(columnFilters)) {
    const match = columnFilters.find(f => {
      const filter = f as IColumnFilter;
      return filter.column_name === dateFieldKey;
    }) as IColumnFilter | undefined;
    if (match) {
      return {
        column_display_cn:
          match.column_display ||
          match.column_name ||
          COLUMN_DISPLAY_TEXT.COMPARE_TIME.CN,
        column_display_en:
          match.column_display_en ||
          match.column_display ||
          match.column_name ||
          COLUMN_DISPLAY_TEXT.COMPARE_TIME.EN,
      };
    }
  }
  return {
    column_display_cn: COLUMN_DISPLAY_TEXT.COMPARE_TIME.CN,
    column_display_en: COLUMN_DISPLAY_TEXT.COMPARE_TIME.EN,
  };
}

/** 维区日期字段：仅打 is_compare_date，不改 column_display_* */
function withCompareTimeFlag(item: IColumnSortItem): IColumnSortItem {
  return {
    ...item,
    is_compare_date: true,
  };
}

function buildStandaloneCompareTimeColumn(
  dateFieldKey: string,
  columnDimensions: unknown[],
  columnFilters?: unknown[],
): IColumnSortItem {
  const meta = resolveCompareTimeFieldMeta(
    dateFieldKey,
    columnDimensions,
    columnFilters,
  );
  const display = resolveCompareTimeFilterDisplay(dateFieldKey, columnFilters);
  return normalizeColumnSortItem({
    table_id: meta.table_id,
    table_code: meta.table_code,
    column: meta.column,
    builder_type: SortableType.Dimension,
    column_display_cn: display.column_display_cn,
    column_display_en: display.column_display_en,
    is_compare_date: true,
  });
}

/**
 * 从 QB `columnDimensions` 构建维度段，并一次处理对比时间。
 *
 * @param columnDimensions - QB 维度区原始字段
 * @param compareTime - 对比时间开关与日期字段 key
 * @param columnFilters - 筛选区（日期仅在筛选区时用于解析独立对比时间列的 table 元数据）
 */
export function buildDimensionSortItemsFromQB(
  columnDimensions: unknown[],
  compareTime?: ICompareTimeInput,
  columnFilters?: unknown[],
): IBuildDimensionSortFromQBResult {
  const dimensions: IColumnSortItem[] = [];
  const dimensionKeySet = new Set<string>();
  /** 对比日期是否已作为维度拖入；为 true 时不再生成 leadingCompareTime */
  let compareDateInDimensions = false;

  const compareEnabled = !!compareTime?.enabled;
  const dateFieldKey = compareTime?.dateFieldKey;

  // ===== 1) 按 QB 维度区顺序生成维度列 =====
  for (const raw of columnDimensions) {
    const row = raw as Record<string, unknown>;

    const isCompareDate =
      compareEnabled && !!dateFieldKey && isRowCompareDateField(row, dateFieldKey);
    let item = qbRowToColumnSortItem(raw, SortableType.Dimension);
    if (!item) {
      continue;
    }
    if (isCompareDate) {
      compareDateInDimensions = true;
      item = withCompareTimeFlag(item);
    }
    dimensions.push(item);
    dimensionKeySet.add(getFieldIdentityKey(row));
  }

  // ===== 2) 对比已开但日期未拖入维度区 → 生成独立「对比时间」前缀列 =====
  const leadingCompareTime: IColumnSortItem[] = [];
  if (compareEnabled && !compareDateInDimensions && dateFieldKey) {
    // table 元数据优先从 columnDimensions 取，否则从 columnFilters 兜底
    leadingCompareTime.push(
      buildStandaloneCompareTimeColumn(
        dateFieldKey,
        columnDimensions,
        columnFilters,
      ),
    );
  }

  return { leadingCompareTime, dimensions, dimensionKeySet };
}
